import { ONE, ZERO } from '@/constants';
import { Typography } from '@/ui';
import { getChoiceText } from '@/utils';

import Pill from './Pill';

import type { SurveyQuestion, SurveyResult } from '@/types';
import type { FC } from 'react';

interface ChoiceProps {
  question: SurveyQuestion;
  results: SurveyResult[];
  // When provided, use aggregated counts instead of per-response results
  aggregated?: { counts: Record<string, number>; totalResponses: number };
}

const Choice: FC<ChoiceProps> = ({ question, results, aggregated }) => {
  const { name = '', options } = question;

  const data = options
    ?.map((option) => {
      if (aggregated) {
        const total = aggregated.counts[option] ?? ZERO;
        return {
          option,
          text: getChoiceText(aggregated.totalResponses, total),
          total,
        };
      }
      const total = results.reduce((totalResult, result) => {
        const hasChoice = result[name]?.choices?.find(
          (choice: string) => choice === option,
        );
        if (hasChoice) return totalResult + ONE;
        return totalResult;
      }, ZERO);
      return { option, text: getChoiceText(results.length, total), total };
    })
    .sort((a, b) => b.total - a.total);

  if (!options || !options.length) return null;

  return (
    <div className="grid lg:grid-cols-2">
      {data?.map(({ option, text }) => (
        <div
          key={`choices-${name}-${option}`}
          className="mb-6 mr-8 flex items-center"
        >
          <Pill text={text} />
          <Typography
            className="overflow-x-hidden text-ellipsis whitespace-nowrap text-bloom-600"
            title={option}
            variant="h6"
          >
            {option}
          </Typography>
        </div>
      ))}
    </div>
  );
};

export default Choice;
